import { useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { useFocusTrap } from "../hooks/use-focus-trap";

export default function Modal({
  title,
  children,
  onClose,
  headerActions,
  className,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  /** Compact icon buttons shown in the title bar, between the title and the close button. */
  headerActions?: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number | null>(null);
  useFocusTrap(ref, onClose);
  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`modal${className ? ` ${className}` : ""}`}
      >
        <div
          className="modal-handle"
          onTouchStart={(e) => {
            touchStartY.current = e.touches[0]?.clientY ?? null;
          }}
          onTouchEnd={(e) => {
            const startY = touchStartY.current;
            touchStartY.current = null;
            const endY = e.changedTouches[0]?.clientY;
            if (startY !== null && endY !== undefined && endY - startY > 45) {
              onClose();
            }
          }}
        />
        <div className="modal-heading">
          <h2>{title}</h2>
          <div className="modal-header-actions">
            {headerActions}
            <button className="icon-button" onClick={onClose} aria-label="Close">
              <X size={21} />
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
